/**
 * Clock sync (SPEC.md §5.2): the scanner asks PageID's server for its time a
 * few times and keeps the sample with the smallest round trip, NTP style:
 *
 *   offset = server − (sent + received) / 2,   error ≤ rtt / 2
 *
 * The corrected clock is `Date.now() + offset`; tick ages are measured on it,
 * so a phone whose clock is seconds off still checks ticks to the 2–3 s limit.
 */
export type ClockSample = { sentAt: number; serverTime: number; receivedAt: number };
export type ClockEstimate = { offsetMs: number; rttMs: number; errorMs: number; samples: number };

export function estimateOffset(samples: ClockSample[]): ClockEstimate | null {
  const good = samples.filter((s) => Number.isFinite(s.serverTime) && s.receivedAt >= s.sentAt);
  if (!good.length) return null;
  const best = good.reduce((a, b) => (b.receivedAt - b.sentAt < a.receivedAt - a.sentAt ? b : a));
  const rtt = best.receivedAt - best.sentAt;
  return { offsetMs: Math.round(best.serverTime - (best.sentAt + best.receivedAt) / 2), rttMs: rtt, errorMs: Math.ceil(rtt / 2), samples: good.length };
}

/** Takes `n` samples one after another (each ≤ timeoutMs) and estimates the offset; null when none came back. */
export async function syncClock(getServerTime: () => Promise<number>, o: { n?: number; now?: () => number } = {}): Promise<ClockEstimate | null> {
  const now = o.now ?? (() => Date.now());
  const samples: ClockSample[] = [];
  for (let i = 0; i < (o.n ?? 5); i++) {
    const sentAt = now();
    try {
      const serverTime = await getServerTime();
      samples.push({ sentAt, serverTime, receivedAt: now() });
    } catch {
      // one lost sample is fine
    }
  }
  return estimateOffset(samples);
}
